import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { FaLinkedin, FaInstagram } from "react-icons/fa";
import { DOCTOR } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const Footer = () => {
  const { t } = useLang();
  return (
  <footer data-testid="site-footer" className="bg-pine-950 text-bone">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-9 h-9 bg-pine-500 text-white flex items-center justify-center font-serif text-xl leading-none">
            +
          </span>
          <span className="font-serif text-2xl font-semibold tracking-tight">Dr. Lokesh Naik G</span>
        </div>
        <p className="text-pine-200/80 text-sm leading-relaxed max-w-sm">
          {t("footer_blurb")}
        </p>
      </div>

      <div className="md:col-span-3">
        <h3 className="text-xs uppercase tracking-[0.25em] font-semibold text-pine-300 mb-6">{t("footer_explore")}</h3>
        <ul className="space-y-3 text-sm">
          {[
            ["fl_home", "/"],
            ["fl_about", "/about"],
            ["fl_treatments", "/treatments"],
            ["fl_guides", "/conditions"],
            ["fl_book", "/book"],
            ["fl_contact", "/contact"],
          ].map(([label, to]) => (
            <li key={to}>
              <Link
                to={to}
                data-testid={`footer-link-${label.replace("fl_", "")}`}
                className="text-pine-100/85 hover:text-white transition-colors duration-300 inline-flex items-center gap-1.5 group"
              >
                {t(label)}
                <ArrowUpRight size={13} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] duration-300" />
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="md:col-span-4">
        <h3 className="text-xs uppercase tracking-[0.25em] font-semibold text-pine-300 mb-6">{t("footer_reach")}</h3>
        <ul className="space-y-4 text-sm">
          <li>
            <a href={DOCTOR.phoneHref} data-testid="footer-phone-link" className="flex items-start gap-3 text-pine-100/85 hover:text-white transition-colors duration-300">
              <Phone size={16} className="mt-0.5 shrink-0 text-pine-300" />
              {DOCTOR.phoneDisplay}
            </a>
          </li>
          <li>
            <a href={DOCTOR.emailHref} data-testid="footer-email-link" className="flex items-start gap-3 text-pine-100/85 hover:text-white transition-colors duration-300 break-all">
              <Mail size={16} className="mt-0.5 shrink-0 text-pine-300" />
              {DOCTOR.email}
            </a>
          </li>
          <li>
            <a href={DOCTOR.emailHospitalHref} data-testid="footer-email-hospital-link" className="flex items-start gap-3 text-pine-100/85 hover:text-white transition-colors duration-300 break-all">
              <Mail size={16} className="mt-0.5 shrink-0 text-pine-300" />
              {DOCTOR.emailHospital}
            </a>
          </li>
          <li className="flex items-start gap-3 text-pine-100/85">
            <MapPin size={16} className="mt-0.5 shrink-0 text-pine-300" />
            <span>
              {DOCTOR.hospital},<br />
              {DOCTOR.hospitalArea}
            </span>
          </li>
          <li className="pt-2 flex items-center gap-3">
            <a
              href={DOCTOR.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin-link"
              aria-label="Dr. Lokesh Naik G on LinkedIn"
              className="inline-flex w-9 h-9 rounded-full bg-white items-center justify-center hover:scale-110 transition-transform duration-300"
            >
              <FaLinkedin size={18} className="text-[#0A66C2]" />
            </a>
            <a
              href={DOCTOR.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              aria-label="Dr. Lokesh Naik G on Instagram"
              className="inline-flex w-9 h-9 rounded-full items-center justify-center text-white hover:scale-110 transition-transform duration-300 bg-[linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)]"
            >
              <FaInstagram size={18} />
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div className="border-t border-pine-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 text-center text-[11px] leading-relaxed text-pine-300/70">
        <span data-testid="footer-copyright">{t("footer_rights")}</span>
        <span className="mx-2 text-pine-500/50">|</span>
        <span data-testid="footer-designer-credit">
          Designed by: Srinivasamurthy P, contact me @{" "}
          <a href="tel:+919845792638" className="hover:text-white transition-colors duration-300">
            +91-9845792638
          </a>
          , Mail:{" "}
          <a href="mailto:puttuseenu.murthy@gmail.com" className="hover:text-white transition-colors duration-300">
            puttuseenu.murthy@gmail.com
          </a>
        </span>
      </div>
    </div>
  </footer>
  );
};

export default Footer;
